import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import api from "@/lib/api";

function Counter({ value, prefix = "", suffix = "", decimals = 0, testid }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (!inView || !value) return undefined;
    const start = performance.now();
    const dur = 1600;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 4);
      setShown(value * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return (
    <span ref={ref} data-testid={testid}>
      {prefix}{shown.toLocaleString("en-AU", { maximumFractionDigits: decimals, minimumFractionDigits: decimals })}{suffix}
    </span>
  );
}

const chipText = (amt) => `A$${amt >= 1000 ? `${(amt / 1000).toFixed(1)}k` : amt.toFixed(0)}`;

export default function ProofStats({ compact = false, testid = "proof-stats" }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats) return null;

  // Only strong numbers are shown — zero/empty tiles hide themselves until they grow
  const supporting = [
    { label: "Clients served worldwide", value: stats.clients_served, testid: "proof-stat-clients" },
    { label: "Client projects & engagements", value: stats.projects + stats.engagements_completed, testid: "proof-stat-projects" },
    { label: "Products across our stores", value: stats.products_total, testid: "proof-stat-products" },
    { label: "Registered members", value: stats.registered_users, testid: "proof-stat-users" },
    { label: "AUD paid to contractors & creators", value: stats.paid_to_people_aud, prefix: "$", testid: "proof-stat-paid" },
    { label: "Verified specialists", value: stats.verified_devs, testid: "proof-stat-devs" },
  ].filter((it) => it.value > 0);
  const chips = stats.top_engagements_aud || [];

  return (
    <section data-testid={testid} className={`relative border-y border-white/10 bg-black overflow-hidden ${compact ? "py-10" : "py-14"}`}>
      {/* animated backdrop — slow-drifting grid + radial glow */}
      <div className="absolute inset-0 grid-texture opacity-40 animate-grid-drift pointer-events-none" />
      <div className="absolute -top-24 left-1/4 w-[500px] h-[300px] bg-nc-blue/10 blur-[120px] rounded-full pointer-events-none animate-pulse-slow" />
      <div className="absolute -bottom-24 right-1/4 w-[400px] h-[250px] bg-nc-orange/8 blur-[110px] rounded-full pointer-events-none animate-pulse-slow" style={{ animationDelay: "1.4s" }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck className="w-4 h-4 text-nc-orange" strokeWidth={1.75} />
          <p className="font-mono text-[10px] tracking-[0.4em] text-nc-orange uppercase">Proof // by the numbers</p>
          <div className="h-px flex-1 bg-gradient-to-r from-nc-orange/40 to-transparent" />
          <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] hidden md:block">Verified company track record + live platform aggregates</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_2fr] gap-10 items-center">
          {/* centerpiece number */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <p className="font-sans font-bold tracking-tight text-6xl md:text-7xl xl:text-8xl text-zinc-50 leading-none">
              <Counter value={stats.processed_aud} prefix="$" testid="proof-stat-processed" />
            </p>
            <p className="font-mono text-[11px] tracking-[0.3em] text-nc-blue uppercase mt-4">AUD processed via Stripe</p>
            <p className="font-mono text-[10px] text-zinc-500 mt-2 leading-relaxed max-w-sm">
              Real client money through real engagements — years of outsourcing work for studios and creators worldwide.
            </p>
          </motion.div>

          {/* supporting stats — only non-zero, staggered reveal */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {supporting.map((it, i) => (
              <motion.div
                key={it.label}
                className="bg-[#070707] px-5 py-6 group hover:bg-[#0c0c0c] transition-colors duration-300"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: 0.15 + i * 0.08 }}
              >
                <p className="font-sans font-bold text-2xl md:text-3xl text-zinc-50 group-hover:text-nc-blue transition-colors duration-300">
                  <Counter value={it.value} prefix={it.prefix || ""} testid={it.testid} />
                </p>
                <p className="font-mono text-[9px] tracking-[0.15em] text-zinc-500 uppercase mt-2 leading-relaxed">{it.label}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* engagement wall — infinite marquee of real deal sizes */}
        {chips.length > 0 && (
          <div data-testid="proof-engagement-wall" className="mt-10 border-t border-white/10 pt-6 overflow-hidden">
            <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase mb-4">Real client engagement values // anonymized</p>
            <div className="relative">
              <div className="flex gap-3 animate-marquee w-max">
                {[...chips, ...chips, ...chips].map((amt, i) => (
                  <span
                    key={i}
                    data-testid={i < chips.length ? `engagement-chip-${i}` : undefined}
                    className="font-mono text-sm text-nc-orange border border-nc-orange/40 bg-nc-orange/5 px-5 py-2.5 whitespace-nowrap hover:bg-nc-orange/15 transition-colors"
                  >
                    {chipText(amt)}
                  </span>
                ))}
              </div>
              <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-black to-transparent pointer-events-none" />
              <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-black to-transparent pointer-events-none" />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
